/**
 * Calendar Sidebar Component
 *
 * Left sidebar showing all posts with filters and search
 * Supports list and grid views with drag-and-drop functionality
 */

import { useState, useCallback, useEffect } from 'react';
import { usePostsStore } from '@/stores/postsStore';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableCellsLarge } from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { Post, PostVariationSummary } from '@/services/posts';
import { DraggableSidebarPostGroup } from './DraggableSidebarPostGroup';
import { DraggableGridPostGroup } from './DraggableGridPostGroup';
import { SettingsDropdown } from './SettingsDropdown';

/** Newly released view: the sidebar lists just these posts and marks the exact new items. */
export interface SidebarHighlight {
  posts: Post[];
  /** `<postId>:base` / `<postId>:<variationId>` of the newly released items */
  newItemKeys: ReadonlySet<string>;
  isLoading: boolean;
  onClear: () => void;
}

interface CalendarSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  posts: Post[];
  totalCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  selectedPostId?: number;
  selectedVariationId?: number;
  onPostSelect: (post: Post) => void;
  onVariationSelect?: (postId: number, variation: PostVariationSummary) => void;
  onOpenFilters?: () => void;
  onEditPost?: (post: Post) => void;
  onDeletePost?: (post: Post) => void;
  onEditVariation?: (postId: number, variation: PostVariationSummary) => void;
  onDeleteVariation?: (postId: number, variation: PostVariationSummary) => void;
  canEditPost?: (post: Post) => boolean;
  canDeletePost?: (post: Post) => boolean;
  onDownloadCalendar?: () => void;
  onResetCalendar?: () => void;
  onManageHolidays?: () => void;
  onImportICS?: () => void;
  onManageMappingRules?: () => void;
  highlight?: SidebarHighlight;
}

type ViewMode = 'list' | 'grid';

/**
 * Skeleton placeholder for list view post
 */
const PostSkeleton = () => (
  <div className="p-3 border-b border-gray-200 animate-pulse">
    <div className="flex gap-3">
      {/* Thumbnail skeleton */}
      <div className="w-12 h-12 bg-gray-200 rounded flex-shrink-0" />
      {/* Content skeleton */}
      <div className="flex-1 min-w-0">
        {/* Caption lines */}
        <div className="h-4 bg-gray-200 rounded w-full mb-2" />
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        {/* Status indicator */}
        <div className="flex items-center gap-2 mt-1">
          <div className="w-6 h-6 bg-gray-200 rounded-md" />
          <div className="h-3 bg-gray-200 rounded w-16" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Skeleton placeholder for grid view post
 */
const GridPostSkeleton = () => (
  <div className="aspect-square rounded-xl bg-gray-200 animate-pulse" />
);

export const CalendarSidebar = ({
  isOpen,
  onToggle,
  posts,
  totalCount,
  hasMore,
  isLoadingMore,
  isLoading,
  onLoadMore,
  selectedPostId,
  selectedVariationId,
  onPostSelect,
  onVariationSelect,
  onOpenFilters,
  onEditPost,
  onDeletePost,
  onEditVariation,
  onDeleteVariation,
  canEditPost,
  canDeletePost,
  onDownloadCalendar,
  onResetCalendar,
  onManageHolidays,
  onImportICS,
  onManageMappingRules,
  highlight,
}: CalendarSidebarProps) => {
  const { t } = useTranslation(['calendar', 'common']);

  // Wire search input to the store's search filter so the backend query refetches
  // with the new term — results beyond the current "load more" boundary are included.
  const storeSearch = usePostsStore((s) => s.filters.search);
  const setFilter = usePostsStore((s) => s.setFilter);
  const [searchInput, setSearchInput] = useState(storeSearch);

  // Sync local input when the store value changes externally (e.g. filter drawer clears it)
  useEffect(() => {
    setSearchInput(storeSearch);
  }, [storeSearch]);

  // Debounce pushing to the store (400ms) to avoid a request on every keystroke
  useEffect(() => {
    if (searchInput === storeSearch) return;
    const t = setTimeout(() => {
      setFilter('search', searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, storeSearch, setFilter]);

  const [viewMode, setViewMode] = useState<ViewMode>(
    typeof window !== 'undefined' && window.innerWidth < 1024 ? 'grid' : 'list'
  );
  const [expandedGridPostId, setExpandedGridPostId] = useState<number | null>(null);

  // Newly released view replaces the regular list while it is on
  const displayPosts = highlight ? highlight.posts : posts;
  const displayLoading = highlight ? highlight.isLoading : isLoading;
  const newItemKeys = highlight?.newItemKeys;

  /**
   * Toggle grid expansion (only one post expanded at a time)
   */
  const handleToggleGridExpand = useCallback((postId: number) => {
    setExpandedGridPostId(prev => prev === postId ? null : postId);
  }, []);

  /**
   * Handle search input change — updates local state immediately for responsiveness;
   * the debounce effect above pushes the value to the store after 400ms.
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  /**
   * Clear search — immediate, no debounce.
   */
  const handleClearSearch = () => {
    setSearchInput('');
    setFilter('search', '');
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Mobile FAB to open sidebar when closed */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className="
            fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+24px+var(--floating-stack-offset))] left-4 z-30 lg:hidden
            w-12 h-12 bg-primary-600 text-primary-600-contrast rounded-full shadow-lg
            flex items-center justify-center
            hover:bg-primary-700 active:scale-95
            transition-all duration-200
          "
          aria-label={t('calendar_sidebar_open')}
        >
          <FontAwesomeIcon icon={faTableCellsLarge} className="text-primary-600-contrast text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          w-72 sm:w-80 bg-white border-r border-gray-200 flex flex-col
          transition-transform duration-300 ease-in-out
          fixed top-[4.1rem] bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] left-0 z-40
          lg:top-0 lg:bottom-0 lg:sticky lg:top-navbar-height lg:h-[calc(100vh-4.1rem)] lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Posts sidebar"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">{t('calendar_sidebar_posts')}</h2>
              <span className="text-xs text-gray-400">
                {totalCount && totalCount > posts.length
                  ? t('calendar_sidebar_posts_of_total', { shown: posts.length, total: totalCount })
                  : `(${posts.length})`}
              </span>
              {onOpenFilters && (
                <Tooltip content={t('filter_posts')}>
                  <button
                    onClick={onOpenFilters}
                    className="-my-1 p-1 hover:bg-gray-100 rounded transition-colors text-gray-500 hover:text-gray-700 inline-flex items-center justify-center"
                    aria-label={t('filter_posts')}
                  >
                    <Icon name={ICONS.FILTER} size="sm" ariaHidden />
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-0.5 mr-2">
                <Tooltip content={t('calendar_sidebar_list_view')}>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white shadow-sm text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    aria-label={t('calendar_sidebar_list_view')}
                  >
                    <Icon name={ICONS.LIST} size="sm" ariaHidden />
                  </button>
                </Tooltip>
                <Tooltip content={t('calendar_sidebar_grid_view')}>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-white shadow-sm text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    aria-label={t('calendar_sidebar_grid_view')}
                  >
                    <Icon name={ICONS.GRID} size="sm" ariaHidden />
                  </button>
                </Tooltip>
              </div>
              {/* Close button (mobile only) */}
              <button
                onClick={onToggle}
                className="p-1 hover:bg-gray-100 rounded lg:hidden"
                aria-label={t('calendar_sidebar_close')}
              >
                <Icon name={ICONS.CROSS} size="sm" ariaHidden />
              </button>
            </div>
          </div>

          {/* Search + Settings (mobile) */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t('calendar_sidebar_search_placeholder')}
                value={searchInput}
                onChange={handleSearchChange}
                className="
                  w-full px-3 py-2 pr-8
                  border border-gray-300 rounded-lg
                  text-sm placeholder-gray-400
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                "
                aria-label={t('calendar_sidebar_search_placeholder')}
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
                  aria-label={t('calendar_sidebar_clear_search')}
                >
                  <Icon name={ICONS.CROSS} size="xs" ariaHidden />
                </button>
              )}
            </div>
            {/* Settings button — mobile only */}
            <div className="flex-shrink-0 lg:hidden">
              <SettingsDropdown
                onDownload={onDownloadCalendar}
                onReset={onResetCalendar}
                onManageHolidays={onManageHolidays}
                onImportICS={onImportICS}
                onManageMappingRules={onManageMappingRules}
              />
            </div>
          </div>
        </div>

        {/* Newly released header */}
        {highlight && (
          <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-primary-100 bg-primary-50 px-4 py-2">
            <h3 className="min-w-0 truncate text-sm font-semibold text-primary-900">
              {t('release_sidebar_newly_released', { number: highlight.newItemKeys.size })}
            </h3>
            <button
              type="button"
              onClick={highlight.onClear}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-primary-700 hover:bg-primary-100"
              aria-label={t('release_sidebar_clear_highlight')}
            >
              <Icon name={ICONS.CROSS} size="xs" ariaHidden />
            </button>
          </div>
        )}

        {/* Posts List/Grid */}
        <div className="flex-1 overflow-y-auto">
              {displayLoading && displayPosts.length === 0 ? (
                // Skeleton loading state
                viewMode === 'list' ? (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </>
                ) : (
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <GridPostSkeleton key={i} />
                    ))}
                  </div>
                )
              ) : displayPosts.length === 0 ? (
                // Empty state
                <div className="p-6 text-center">
                  <div className="text-gray-400 mb-2">
                    <Icon name={ICONS.SEARCH} size="lg" ariaHidden />
                  </div>
                  <p className="text-sm text-gray-500">
                    {searchInput && !highlight
                      ? t('calendar_sidebar_no_match')
                      : t('calendar_sidebar_no_posts')}
                  </p>
                </div>
              ) : viewMode === 'list' ? (
                // List View
                displayPosts.map((post) => (
                  <DraggableSidebarPostGroup
                    key={post.id}
                    post={post}
                    newItemKeys={newItemKeys}
                    isSelected={post.id === selectedPostId}
                    selectedVariationId={selectedVariationId}
                    onClick={() => onPostSelect(post)}
                    onVariationClick={(variation) => onVariationSelect?.(post.id, variation)}
                    onEdit={onEditPost && (!canEditPost || canEditPost(post)) ? () => onEditPost(post) : undefined}
                    onDelete={onDeletePost && (!canDeletePost || canDeletePost(post)) ? () => onDeletePost(post) : undefined}
                    onEditVariation={(variation) => onEditVariation?.(post.id, variation)}
                    onDeleteVariation={(variation) => onDeleteVariation?.(post.id, variation)}
                  />
                ))
              ) : (
                // Grid View - 3 cols
                <div className="grid grid-cols-3 gap-2 p-2">
                  {displayPosts.map((post) => (
                    <DraggableGridPostGroup
                      key={post.id}
                      post={post}
                      newItemKeys={newItemKeys}
                      isSelected={post.id === selectedPostId}
                      selectedVariationId={selectedVariationId}
                      isExpanded={expandedGridPostId === post.id}
                      onToggleExpand={() => handleToggleGridExpand(post.id)}
                      onClick={() => onPostSelect(post)}
                      onVariationClick={(variation) => onVariationSelect?.(post.id, variation)}
                      onEdit={onEditPost && (!canEditPost || canEditPost(post)) ? () => onEditPost(post) : undefined}
                      onDelete={onDeletePost && (!canDeletePost || canDeletePost(post)) ? () => onDeletePost(post) : undefined}
                      onEditVariation={(variation) => onEditVariation?.(post.id, variation)}
                      onDeleteVariation={(variation) => onDeleteVariation?.(post.id, variation)}
                    />
                  ))}
                </div>
              )}

              {/* Load More Posts Button */}
              {!highlight && posts.length > 0 && hasMore && onLoadMore && (
                <div className="p-4 border-t border-gray-100">
                  <button
                    onClick={onLoadMore}
                    disabled={isLoadingMore}
                    className="w-full py-2 px-4 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoadingMore ? (
                      <>
                        <Icon name={ICONS.SPINNER} size="sm" className="animate-spin" ariaHidden />
                        {t('loading')}
                      </>
                    ) : (
                      <>
                        <Icon name={ICONS.ANGLE_DOWN} size="sm" ariaHidden />
                        {t('load_more')}
                      </>
                    )}
                  </button>
                </div>
              )}
        </div>
      </aside>
    </>
  );
};
